import React, { useState, useRef } from 'react';
import apiService from '../../../services/api';
import './FileUpload.css';

const FileUpload = ({
  value,
  onChange,
  error,
  disabled,
  accept = ".pdf,.doc,.docx",
  maxSize = 10 * 1024 * 1024, // 10MB
  placeholder = "Choose file or drag and drop",
  multiple = false,
  documentType // Required for backend upload
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      return `File size exceeds ${Math.round(maxSize / (1024 * 1024))}MB limit`;
    }

    // Check file type
    const allowedTypes = accept.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const mimeTypeMap = {
      '.pdf': 'application/pdf',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };

    const isValidExtension = allowedTypes.includes(fileExtension);
    const isValidMimeType = allowedTypes.includes(file.type) ||
                           Object.values(mimeTypeMap).includes(file.type);

    if (!isValidExtension && !isValidMimeType) {
      return `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0]; // Take first file even if multiple is true for now
    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    if (!documentType) {
      setUploadError('Document type is required for file upload');
      return;
    }

    setUploadError('');
    setIsUploading(true);

    try {
      // Upload file to backend
      const uploadResult = await apiService.uploadFile(file, documentType);

      if (uploadResult.success) {
        // Pass file metadata to parent component instead of File object
        const fileMetadata = {
          file_id: uploadResult.data.fileId,
          file_name: uploadResult.data.fileName,
          file_size: uploadResult.data.fileSize,
          file_type: uploadResult.data.fileType,
          uploaded_at: uploadResult.data.uploadedAt,
          original_file: file // Keep reference for display purposes
        };
        onChange(fileMetadata);
      } else {
        setUploadError(uploadResult.message || 'Failed to upload file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError('Network error occurred while uploading file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileSelect(files);
  };

  const handleClick = () => {
    if (!disabled && !isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setUploadError('');
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const displayError = error || uploadError;

  // Check if value is file metadata or File object
  const isFileMetadata = value && typeof value === 'object' && value.file_id;
  const isFileObject = value && value instanceof File;
  const hasFile = isFileMetadata || isFileObject;
  const fileName = isFileMetadata ? value.file_name : (isFileObject ? value.name : '');
  const fileSize = isFileMetadata ? value.file_size : (isFileObject ? value.size : 0);

  return (
    <div className={`file-upload ${error ? 'error' : ''} ${disabled || isUploading ? 'disabled' : ''}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        disabled={disabled || isUploading}
        className="file-input-hidden"
      />

      <div
        className={`file-upload-area ${isDragOver ? 'drag-over' : ''} ${hasFile ? 'has-file' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isUploading) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {isUploading ? (
          <div className="upload-prompt">
            <div className="upload-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6v6l4-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="upload-text">
              <p className="upload-primary">Uploading file...</p>
              <p className="upload-secondary">Please wait while your file is being uploaded</p>
            </div>
          </div>
        ) : !hasFile ? (
          <div className="upload-prompt">
            <div className="upload-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 10L12 5L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 5V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="upload-text">
              <p className="upload-primary">{placeholder}</p>
              <p className="upload-secondary">
                {accept.replace(/\./g, '').toUpperCase()} files up to {Math.round(maxSize / (1024 * 1024))}MB
              </p>
            </div>
            <button type="button" className="upload-button" disabled={disabled || isUploading}>
              Choose File
            </button>
          </div>
        ) : (
          <div className="file-info">
            <div className="file-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="file-details">
              <p className="file-name">{fileName || 'Unknown file'}</p>
              <p className="file-size">{fileSize ? formatFileSize(fileSize) : 'Unknown size'}</p>
              {isFileMetadata && (
                <p className="file-status">✓ Uploaded successfully</p>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                className="remove-file-btn"
                onClick={handleRemove}
                aria-label="Remove file"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {displayError && (
        <div className="file-upload-error">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
